import { useEffect, useState } from "react";
import { Loader2, Save, Settings, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useConnectPolicy, useTenants, useUpdateConnectPolicy } from "@/api/hooks";
import type { ConnectPolicy } from "@/api/adminBff";

const TOGGLES: Array<{
    key: keyof Omit<ConnectPolicy, "tenantId" | "updatedAt">;
    label: string;
    help: string;
}> = [
    {
        key: "allowRecording",
        label: "Allow meeting recording",
        help: "When off, the recording button is hidden in meetings and the BFF rejects new recording metadata."
    },
    {
        key: "allowFileSharing",
        label: "Allow file sharing",
        help: "Controls whether channel and chat attachments can be uploaded."
    },
    {
        key: "allowIncomingWebhooks",
        label: "Allow incoming webhooks",
        help: "Per-channel connectors that let external services post into a channel."
    },
    {
        key: "allowChannelTabs",
        label: "Allow channel tabs",
        help: "Pinned URL embeds in the channel header (iframes)."
    },
    {
        key: "allowFederation",
        label: "Allow federation",
        help: "Cross-deployment messaging (Athavita ↔ another Athavita instance)."
    }
];

/**
 * Microsoft Teams-style admin policy panel for Connect. Per-tenant
 * toggles that gate user-visible features. Policies are read by the
 * Connect BFF at request time via AdminPolicyReadDbContext, so changes
 * propagate within ~60 s (the BFF's policy cache TTL).
 */
export function ConnectPoliciesPage() {
    const tenants = useTenants();
    const [tenantId, setTenantId] = useState<string | null>(null);

    useEffect(() => {
        if (!tenantId && tenants.data && tenants.data.length > 0) {
            setTenantId(tenants.data[0]!.id);
        }
    }, [tenants.data, tenantId]);

    return (
        <div className="mx-auto max-w-3xl">
            <header className="mb-4 flex items-center gap-2">
                <ShieldCheck className="h-6 w-6 text-primary" />
                <h1 className="text-2xl font-semibold">Connect policies</h1>
            </header>
            <p className="mb-4 text-sm text-muted-foreground">
                Tenant-scoped feature toggles for Athavita Connect. Changes apply
                to every member of the selected tenant within ~60 seconds.
            </p>

            <div className="mb-4">
                <label className="mb-1 block text-xs font-medium">Tenant</label>
                <select
                    value={tenantId ?? ""}
                    onChange={(e) => setTenantId(e.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                    <option value="" disabled>Select a tenant…</option>
                    {(tenants.data ?? []).map((t) => (
                        <option key={t.id} value={t.id}>{t.displayName} ({t.slug})</option>
                    ))}
                </select>
            </div>

            {tenantId ? <PolicyForm tenantId={tenantId} /> : null}
        </div>
    );
}

function PolicyForm({ tenantId }: { tenantId: string }) {
    const policy = useConnectPolicy(tenantId);
    const update = useUpdateConnectPolicy(tenantId);
    const [draft, setDraft] = useState<ConnectPolicy | null>(null);

    useEffect(() => {
        if (policy.data) setDraft(policy.data);
    }, [policy.data]);

    if (policy.isPending || !draft) {
        return (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading policy…
            </div>
        );
    }

    const dirty =
        policy.data !== undefined
        && (Object.keys(draft) as Array<keyof ConnectPolicy>)
            .some((k) => policy.data && draft[k] !== policy.data[k]);

    return (
        <section className="rounded-lg border bg-card shadow-sm">
            <header className="flex items-center gap-2 border-b px-5 py-3">
                <Settings className="h-4 w-4 text-muted-foreground" />
                <h2 className="font-semibold">Feature toggles</h2>
                <Button
                    size="sm"
                    onClick={() => update.mutate(draft)}
                    disabled={!dirty || update.isPending}
                    className="ml-auto"
                >
                    {update.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    Save
                </Button>
            </header>
            <ul className="divide-y">
                {TOGGLES.map((t) => (
                    <li key={t.key} className="flex items-start gap-3 px-5 py-3">
                        <input
                            type="checkbox"
                            id={`policy-${t.key}`}
                            checked={Boolean(draft[t.key])}
                            onChange={(e) => setDraft({ ...draft, [t.key]: e.target.checked })}
                            className="mt-0.5 h-4 w-4"
                        />
                        <div className="flex-1">
                            <label htmlFor={`policy-${t.key}`} className="text-sm font-medium">
                                {t.label}
                            </label>
                            <p className="text-xs text-muted-foreground">{t.help}</p>
                        </div>
                    </li>
                ))}
            </ul>
            {policy.data?.updatedAt ? (
                <footer className="border-t px-5 py-2 text-xs text-muted-foreground">
                    Last updated {new Date(policy.data.updatedAt).toLocaleString()}
                </footer>
            ) : null}
        </section>
    );
}
